import React from 'react';
import { Tag, Tooltip } from 'antd';

/**
 * Single home of the labels that explain WHY an order's result is missing or
 * incomplete on the result screens (Analiza, Prognoza a wynik, Rachunek wyników).
 * A label appears only when something is actually missing — a complete order shows
 * nothing. Wording: reports:profitability.costGaps.*.
 */
export type CostGapKind = 'NO_SALE_PRICES' | 'NO_COST_OF_SALES' | 'NO_PRODUCTS' | 'MANUAL_PRODUCTS'
  // SZ.9: a confirmation draft ZD priced at the average purchase cost, with a line that has none.
  | 'NO_AVERAGE_PURCHASE_COST' | 'AVERAGE_UNIT_UNRESOLVED' | 'AVERAGE_RATE_UNAVAILABLE';

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

const KEYS: Record<CostGapKind, string> = {
  NO_SALE_PRICES: 'noSalePrices',
  NO_COST_OF_SALES: 'noCostOfSales',
  NO_PRODUCTS: 'noProducts',
  MANUAL_PRODUCTS: 'manualProducts',
  NO_AVERAGE_PURCHASE_COST: 'noAveragePurchaseCost',
  AVERAGE_UNIT_UNRESOLVED: 'averageUnitUnresolved',
  AVERAGE_RATE_UNAVAILABLE: 'averageRateUnavailable',
};

const REASON_TO_KIND: Record<string, CostGapKind> = {
  MISSING_REVENUE: 'NO_SALE_PRICES',
  MISSING_COSTS: 'NO_COST_OF_SALES',
  NO_PRODUCTS: 'NO_PRODUCTS',
  MANUAL_PRODUCTS: 'MANUAL_PRODUCTS',
  // WP5 average-cost reasons (SZ.9 draft rows priced at the average purchase cost).
  NO_AVERAGE_COST: 'NO_AVERAGE_PURCHASE_COST',
  AVERAGE_UNIT_UNRESOLVED: 'AVERAGE_UNIT_UNRESOLVED',
  AVERAGE_RATE_UNAVAILABLE: 'AVERAGE_RATE_UNAVAILABLE',
};

/** Maps a backend review reason to a gap label, or `null` when it is another kind of anomaly. */
export function gapKindForReason(reason: string): CostGapKind | null {
  return REASON_TO_KIND[reason] ?? null;
}

// Mirror of backend customerOrderStatusPolicy (draft, sent offer and cancelled are never
// forecast): their missing data is not a problem yet, so they get no label.
const NOT_IN_FORECAST_STATUSES = ['NEW_INQUIRY', 'QUOTE_SENT', 'CANCELLED'];

interface GapFacts {
  status?: string | null;
  revenue?: number | null;
  /** Incomplete plan cost: the order carries only its known part (owner decision 2026-09-30). */
  costIncomplete?: boolean;
}

/** Which labels an order needs. Empty for a complete order. */
export function orderCostGaps(facts: GapFacts): CostGapKind[] {
  if (facts.status && NOT_IN_FORECAST_STATUSES.includes(facts.status)) return [];
  const gaps: CostGapKind[] = [];
  if (facts.revenue != null && facts.revenue <= 0) gaps.push('NO_SALE_PRICES');
  if (facts.costIncomplete) gaps.push('NO_COST_OF_SALES');
  return gaps;
}

export function CostGapTag({ kind, t }: { kind: CostGapKind; t: TranslateFn }) {
  const base = `reports:profitability.costGaps.${KEYS[kind]}`;
  return (
    <Tooltip title={t(`${base}.hint`)}>
      <Tag color="warning" data-testid={`cost-gap-${KEYS[kind]}`} style={{ marginInlineEnd: 0 }}>{t(`${base}.label`)}</Tag>
    </Tooltip>
  );
}

export function CostGapTags({ kinds, t }: { kinds: CostGapKind[]; t: TranslateFn }) {
  if (kinds.length === 0) return null;
  return (
    <span className="cost-gap-tags" style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 4 }}>
      {kinds.map((kind) => <CostGapTag key={kind} kind={kind} t={t} />)}
    </span>
  );
}
